import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PawPrint, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

const SIZES = ["Small", "Medium", "Large"];
const GENDERS = ["Female", "Male"];
const PERSONALITIES = ["Friendly", "Playful", "Loves water", "Shy at first", "Energetic", "Chill"];

const Onboarding = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [breed, setBreed] = useState("");
  const [age, setAge] = useState("");
  const [size, setSize] = useState("Medium");
  const [gender, setGender] = useState("Female");
  const [traits, setTraits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const toggle = (t) => setTraits((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/dogs", {
        name,
        breed,
        age: age ? parseInt(age, 10) : null,
        size,
        gender,
        personality: traits,
      });
      toast.success(`${name} added to your pack!`);
      // Funnel instrumentation — fire BEFORE navigate so the pageview
      // that follows can chain to it in GA4's real-time funnel. Matches
      // the shape of `onboarding_skipped` so both branches are directly
      // comparable in reporting.
      track("onboarding_completed", {
        size,
        gender,
        personality_count: traits.length,
      });
      // Take committed users straight to the location-aware search
      // flow instead of the stats scoreboard on /profile. The
      // `?nearMe=1` param is picked up by Search.jsx which will auto-
      // trigger geolocation on first arrival (see the `nearMe` effect
      // there). If the user denies permission, Search falls back to
      // its default place list — no error state.
      navigate("/search?nearMe=1");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not save dog");
    } finally {
      setSaving(false);
    }
  };

  // "Skip for now" — sets onboarding_skipped on the user doc, fires
  // the GA4 funnel event, then drops them on the search/Explore page
  // (same destination as the happy-path save). Failures of the
  // backend write are non-blocking: we still navigate so the user
  // never gets stuck on this screen.
  const handleSkip = async () => {
    if (skipping || saving) return;
    setSkipping(true);
    track("onboarding_skipped");
    try {
      await api.post("/auth/skip-onboarding");
    } catch {
      // Non-fatal — the worst case is the Profile nudge re-shows next visit.
    } finally {
      navigate("/search");
    }
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] py-10">
      <div className="max-w-xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-3xl border border-brand-border-warm p-6 sm:p-8 shadow-warm animate-fade-in-up" data-testid="onboarding">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-10 h-10 rounded-full bg-brand-light-green text-brand-forest flex items-center justify-center">
              <PawPrint className="w-5 h-5" />
            </span>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-forest">Add your dog</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink tracking-tight leading-none">
            Tell us about your pup
          </h1>
          <p className="text-sm text-brand-ink/60 mt-2">So other pet parents can say hi at the park.</p>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Dog's name</span>
                <input
                  data-testid="dog-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-1.5 px-4 py-3 bg-brand-cream border border-brand-border-warm rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Breed</span>
                <input
                  data-testid="dog-breed"
                  required
                  value={breed}
                  onChange={(e) => setBreed(e.target.value)}
                  placeholder="Golden Retriever"
                  className="w-full mt-1.5 px-4 py-3 bg-brand-cream border border-brand-border-warm rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Age (years)</span>
                <input
                  data-testid="dog-age"
                  type="number"
                  min="0"
                  max="30"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full mt-1.5 px-4 py-3 bg-brand-cream border border-brand-border-warm rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
              </label>
              <div>
                <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Gender</span>
                <div className="flex gap-2 mt-1.5">
                  {GENDERS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      data-testid={`dog-gender-${g.toLowerCase()}`}
                      onClick={() => setGender(g)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        gender === g ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-border-warm"
                      }`}
                    >{g}</button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Size</span>
              <div className="flex gap-2 mt-1.5">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`dog-size-${s.toLowerCase()}`}
                    onClick={() => setSize(s)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                      size === s ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-border-warm"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold text-brand-ink uppercase tracking-wider">Personality</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {PERSONALITIES.map((t) => {
                  const on = traits.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`dog-trait-${t.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => toggle(t)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        on ? "bg-brand-forest text-white border-brand-forest" : "bg-white text-brand-ink border-brand-border-warm hover:border-brand-forest"
                      }`}
                    >
                      {on && <Check className="w-3 h-3 inline mr-1" />}
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              data-testid="onboarding-submit"
              disabled={saving || skipping}
              className="w-full bg-brand-forest text-white font-semibold rounded-full py-3.5 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save and continue
            </button>
            {/* "Skip for now" — text link, intentionally quiet so it
                doesn't steal clicks from the primary CTA above. Muted
                olive-grey + underline matches the spec exactly. */}
            <div className="text-center -mt-1">
              <button
                type="button"
                data-testid="onboarding-skip"
                onClick={handleSkip}
                disabled={saving || skipping}
                className="font-body underline hover:no-underline disabled:opacity-60"
                style={{
                  color: "#7A7A60",
                  fontSize: 14,
                  background: "none",
                  border: 0,
                  padding: "4px 8px",
                  cursor: skipping || saving ? "not-allowed" : "pointer",
                }}
              >
                {skipping ? "Skipping…" : "Skip for now"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
